// Page admin de gestion des messages système
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import MessageComposer from '../components/admin/MessageComposer';
import {
  getAllMessages,
  deleteMessage,
  updateMessage,
  getMessageStats,
} from '../Services/systemMessageService';
import {
  getMessageIcon,
  getMessageBgClass,
  getMessageTypeLabel,
  getAudienceLabel,
  getPriorityLabel,
  getRelativeTime,
} from '../utils/messageHelpers';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  BarChart2,
  Loader2,
  MessageSquare,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// ─── Statut d'un message ────────────────────────────────
const getMessageStatus = (msg) => {
  const now = new Date();
  if (!msg.publishedAt) return { label: 'Brouillon', class: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' };

  const publishedDate = msg.publishedAt?.toDate?.() || new Date(msg.publishedAt);
  const expiresDate = msg.expiresAt?.toDate?.() || null;

  if (expiresDate && expiresDate < now) {
    return { label: 'Expiré', class: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' };
  }
  if (publishedDate > now) {
    return { label: 'Planifié', class: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' };
  }
  return { label: 'Publié', class: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' };
};

// ─── Composant Stats ────────────────────────────────────
const StatsModal = ({ message, open, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && message?.id) {
      setLoading(true);
      getMessageStats(message.id)
        .then(setStats)
        .finally(() => setLoading(false));
    }
  }, [open, message?.id]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-gray-900 dark:text-gray-100">
            Statistiques
          </DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
            {message?.title}
          </p>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {stats?.readCount ?? 0}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                lecture{stats?.readCount !== 1 ? 's' : ''}
              </p>
            </div>
            {stats?.lastReadAt && (
              <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                Dernière lecture :{' '}
                <span className="font-medium">
                  {stats.lastReadAt.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Page principale ─────────────────────────────────────
const AdminMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editMessage, setEditMessage] = useState(null);
  const [statsMessage, setStatsMessage] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Charger les messages
  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllMessages();
      setMessages(data);
    } catch {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Ouvrir le composer pour un nouveau message
  const handleNewMessage = () => {
    setEditMessage(null);
    setComposerOpen(true);
  };

  // Ouvrir le composer en mode édition
  const handleEdit = (msg) => {
    setEditMessage(msg);
    setComposerOpen(true);
  };

  // Supprimer un message
  const handleDelete = async (msg) => {
    if (!window.confirm(`Supprimer "${msg.title}" ? Cette action est irréversible.`)) return;
    setDeletingId(msg.id);
    try {
      await deleteMessage(msg.id);
      toast.success('Message supprimé');
      await loadMessages();
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  // Dépublier un message (remettre en brouillon)
  const handleUnpublish = async (msg) => {
    try {
      await updateMessage(msg.id, msg, false);
      toast.success('Message dépublié');
      await loadMessages();
    } catch {
      toast.error('Erreur lors de la dépublication');
    }
  };

  // Publier un brouillon
  const handlePublish = async (msg) => {
    try {
      await updateMessage(msg.id, msg, true);
      toast.success('Message publié !');
      await loadMessages();
    } catch {
      toast.error('Erreur lors de la publication');
    }
  };

  // Ouvrir les stats
  const handleStats = (msg) => {
    setStatsMessage(msg);
    setStatsOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Messages système
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {messages.length} message{messages.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
  <Button
    variant="outline"
    size="sm"
    onClick={loadMessages}
    disabled={loading}
    className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
  >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={handleNewMessage}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau message
            </Button>
          </div>
        </div>

        {/* Tableau des messages */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Aucun message
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Créez votre premier message système.
              </p>
              <Button onClick={handleNewMessage} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Créer un message
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                      Titre
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      Type
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      Audience
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      Statut
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      Publication
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {messages.map((msg) => {
                    const status = getMessageStatus(msg);
                    const isDeleting = deletingId === msg.id;
                    return (
                      <tr
                        key={msg.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        {/* Titre */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-base">{getMessageIcon(msg.type)}</span>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100 line-clamp-1 max-w-xs">
                                {msg.title}
                              </p>
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                {getPriorityLabel(msg.priority)}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getMessageBgClass(msg.type)}`}
                          >
                            {getMessageTypeLabel(msg.type)}
                          </span>
                        </td>

                        {/* Audience */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {getAudienceLabel(msg.targetAudience)}
                          </span>
                        </td>

                        {/* Statut */}
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.class}`}
                          >
                            {status.label}
                          </span>
                        </td>

                        {/* Date publication */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {msg.publishedAt ? getRelativeTime(msg.publishedAt) : '—'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Stats */}
                            <button
                              onClick={() => handleStats(msg)}
                              title="Statistiques"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              <BarChart2 className="w-4 h-4" />
                            </button>

                            {/* Publier / Dépublier */}
                            {msg.publishedAt ? (
                              <button
                                onClick={() => handleUnpublish(msg)}
                                title="Dépublier"
                                className="p-1.5 rounded-lg text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                              >
                                <EyeOff className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePublish(msg)}
                                title="Publier"
                                className="p-1.5 rounded-lg text-gray-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}

                            {/* Modifier */}
                            <button
                              onClick={() => handleEdit(msg)}
                              title="Modifier"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>

                            {/* Supprimer */}
                            <button
                              onClick={() => handleDelete(msg)}
                              title="Supprimer"
                              disabled={isDeleting}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                            >
                              {isDeleting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Composer (créer/modifier) */}
      <MessageComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSuccess={loadMessages}
        editMessage={editMessage}
      />

      {/* Modal stats */}
      <StatsModal
        message={statsMessage}
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
      />
    </AdminLayout>
  );
};

export default AdminMessagesPage;
